
import React, { useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { classNames } from '../../helpers/classNames';

/**
 * DropTarget component — a reusable react-dnd drop area.
 *
 * @param {Object} props
 * @param {string|string[]|Symbol} props.accept - The type(s) of draggable items this target accepts (passed to react-dnd `useDrop`).
 * @param {(item: any) => void} props.onDrop - Called when an accepted item is dropped; receives the dropped item.
 * @param {(isOver: boolean) => void} [props.onIsOver] - Optional. Called when the hover state changes while the drop target can accept the item.
 * @param {React.ReactNode} props.children - Content rendered inside the drop target.
 * @param {string} [props.isActiveStyling] - ClassName(s) applied when the drop target is active (canDrop && isOver).
 * @param {string} [props.className] - Additional className(s) applied to the root element.
 * @returns {JSX.Element} A div element wired to react-dnd's useDrop (ref attached via `drop`). The component computes `isActive` as `canDrop && isOver` and invokes `onIsOver` only when `canDrop` is true and `isOver` changes.
 */
const DropTarget = ({ accept, onDrop, onIsOver, children, isActiveStyling,className }) => {
    const [{ canDrop, isOver }, drop] = useDrop({
        accept,
        drop: (item) => {
            onDrop(item);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        })
    });

    const isActive = canDrop && isOver;

    // Hook to handle isOver changes
    useEffect(() => {
        if (!canDrop) return
        if (onIsOver && typeof onIsOver === 'function') {
            onIsOver(isOver);
        }
    }, [isOver]);

    return (
        <div
            ref={drop}
            style={{}}
            className={classNames(className,isActive && isActiveStyling, 'transition-all duration-200 ease-in-out')}
        >
            {children}
        </div>
    );
};

export default DropTarget;
